import React from 'react';
import PropTypes from 'prop-types';
import useHybridSearchEnabled from 'hooks/useHybridSearchEnabled';
import ResultsTitle from '../../../ui/atoms/ResultsTitle';
import ClosedTripItem from '../ClosedTripItem';
import { shouldGroupTerminalResults } from '../../../utils/search/terminalResultGroups';

/**
 * ClosedTripsSection component.
 * Renders a section displaying closed/unavailable trips (sold out or departed).
 * @param {object} props - trips, filtersApplied, features, origin, destination.
 * @param {Array} props.closedTrips - Array of closed trip objects.
 * @param {Function} props.selectTrip - Function to handle trip selection (disabled for closed trips).
 * @param {boolean} props.isRoundTrip - Whether this is a round trip search.
 * @param {string} props.way - Trip direction ('departure' or 'return').
 * @param {boolean} props.isLogged - Whether user is logged in.
 * @param {object} props.departureTrip - Departure trip data (for round trips).
 * @param {object} props.rewardTicketStatus - Reward ticket status.
 * @param {Function} props.setRewardTicketStatus - Function to set reward ticket status.
 * @param {object} props.filtersApplied - Applied filters object.
 * @param {object} props.features - Feature flags object.
 * @param {object} props.origin - Origin location object.
 * @param {object} props.destination - Destination location object.
 * @param {boolean} props.hideWhenTerminalGrouped - Hide the global section for terminal groups.
 * @returns {JSX.Element|null} The rendered ClosedTripsSection component or null if no closed trips.
 */
const ClosedTripsSection = ({
  closedTrips,
  selectTrip,
  isRoundTrip,
  way,
  isLogged,
  departureTrip,
  rewardTicketStatus,
  setRewardTicketStatus,
  filtersApplied,
  features,
  origin,
  destination,
  hideWhenTerminalGrouped,
}) => {
  const hybridSearchEnabled = useHybridSearchEnabled();
  const isTerminalGrouped = shouldGroupTerminalResults({
    featureEnabled: hybridSearchEnabled,
    origin,
    destination,
    isHorizontal: false,
    isRecommendedList: false,
  });

  if (!closedTrips || closedTrips.length === 0) return null;
  if (hideWhenTerminalGrouped && isTerminalGrouped) return null;

  return (
    <div className="trip-container">
      <ResultsTitle sectionTitle="closedSchedules" />

      <div className="space-y-3">
        {closedTrips.map((trip, index) => (
          <ClosedTripItem
            key={trip.id}
            trip={trip}
            position={index}
            selectTrip={selectTrip}
            isRoundTrip={isRoundTrip}
            way={way}
            isLogged={isLogged}
            departureTrip={departureTrip}
            rewardTicketStatus={rewardTicketStatus}
            setRewardTicketStatus={setRewardTicketStatus}
            filtersApplied={filtersApplied}
            features={features}
            origin={origin}
            destination={destination}
          />
        ))}
      </div>
    </div>
  );
};

ClosedTripsSection.propTypes = {
  closedTrips: PropTypes.array,
  selectTrip: PropTypes.func.isRequired,
  isRoundTrip: PropTypes.bool,
  way: PropTypes.string.isRequired,
  isLogged: PropTypes.bool,
  departureTrip: PropTypes.object,
  rewardTicketStatus: PropTypes.object,
  setRewardTicketStatus: PropTypes.func,
  filtersApplied: PropTypes.object,
  features: PropTypes.object,
  origin: PropTypes.object,
  destination: PropTypes.object,
  hideWhenTerminalGrouped: PropTypes.bool,
};

ClosedTripsSection.defaultProps = {
  closedTrips: [],
  isRoundTrip: false,
  isLogged: false,
  departureTrip: null,
  rewardTicketStatus: {},
  setRewardTicketStatus: () => {},
  filtersApplied: {},
  features: {},
  origin: null,
  destination: null,
  hideWhenTerminalGrouped: false,
};

export default ClosedTripsSection;
